/**
 * Resize / re-encode an image File before uploading to the OCR endpoint.
 *
 * Big phone photos (often 8–12 MB, 12 MP) make GPT-5.2 vision slow enough to
 * trip the proxy's 504 timeout. The model doesn't need that resolution to read
 * a pharmacy label, so we downscale to a sensible max edge and re-encode as
 * JPEG. Returns the original File untouched when it's already small.
 */

const MAX_EDGE_PX = 1600;       // Plenty for a pill bottle close-up.
const MAX_BYTES_BEFORE_RESIZE = 600 * 1024; // < 600 KB → don't bother.
const JPEG_QUALITY = 0.85;

export async function downsizeImage(file) {
  try {
    if (!file || !file.type || !file.type.startsWith("image/")) return file;
    if (file.size <= MAX_BYTES_BEFORE_RESIZE) return file;

    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = dataUrl;
    });

    const longEdge = Math.max(img.width, img.height);
    if (longEdge <= MAX_EDGE_PX) return file;

    const scale = MAX_EDGE_PX / longEdge;
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", JPEG_QUALITY));
    if (!blob) return file;
    // Replace extension with .jpg since we re-encoded.
    const baseName = (file.name || "rx").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}-resized.jpg`, { type: "image/jpeg" });
  } catch {
    // If resize fails for any reason, fall back to the original. The backend
    // still has its own 8 MB cap and timeout to defend against bad inputs.
    return file;
  }
}
